import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  ActionCodeSettings,
  User,
  sendEmailVerification, // Importuj
  updatePassword,
  // updateEmail, // Not used currently
  GoogleAuthProvider, // Import Google provider
  signInWithPopup, // Import popup function
  reauthenticateWithPopup,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset as confirmPasswordResetInAuth,
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import type {
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  TotpSecret,
} from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../firebase';
import { UserRole } from '../types/firestore';
import {
  ACCOUNT_PASSWORD_POLICY_COPY,
  TotpEnrollmentDraft,
  validateAccountPassword,
} from './accountSecurity';

interface ProvisionUserProfileOnSignupRequest {
  role: 'client' | 'serviceAdmin';
  displayName?: string;
}

interface ProvisionUserProfileOnSignupResponse {
  success: boolean;
  profileState: 'created' | 'patched' | 'already-exists';
}

export interface VerificationEmailResult {
  success: boolean;
  errorCode?: string;
  userMessage?: string;
  requestId?: string;
  source?: VerificationEmailSource;
}

export interface RegisterResult {
  user: User;
  verificationEmail: VerificationEmailResult;
}

export interface PasswordResetRequestResult {
  success: boolean;
  status: 'sent' | 'user-not-found' | 'failed';
  errorCode?: string;
  userMessage: string;
}

export interface TotpSignInHint {
  uid: string;
  displayName: string | null;
  factorId: string;
}

export interface TotpSignInChallenge {
  kind: 'totp-sign-in';
  resolver: MultiFactorResolver;
  hints: TotpSignInHint[];
  selectedEnrollmentId: string;
}

export interface MfaRequiredSignInError extends Error {
  code: 'auth/multi-factor-auth-required';
  mfaChallenge: TotpSignInChallenge;
  originalError: unknown;
}

export interface TotpMfaReadiness {
  sdkSupportsTotp: boolean;
  deploymentConfigured: boolean;
  canManageEnrollment: boolean;
  enrolledFactorsCount: number;
  hasTotpEnrollment: boolean;
  status: 'active' | 'not-enrolled' | 'not-configured' | 'unavailable';
}

const provisionUserProfileOnSignup = httpsCallable<
  ProvisionUserProfileOnSignupRequest,
  ProvisionUserProfileOnSignupResponse
>(functions, 'provisionUserProfileOnSignup');

type VerificationEmailSource = 'register' | 'resend';

interface VerificationEmailOptions {
  source: VerificationEmailSource;
}

const buildVerificationActionCodeSettings = (): ActionCodeSettings | undefined => {
  const configuredContinueUrl = String(
    process.env.REACT_APP_VERIFY_EMAIL_CONTINUE_URL || ''
  ).trim();
  const configuredDynamicLinkDomain = String(
    process.env.REACT_APP_VERIFY_EMAIL_DYNAMIC_LINK_DOMAIN || ''
  ).trim();

  if (!configuredContinueUrl && !configuredDynamicLinkDomain) {
    return undefined;
  }

  const defaultContinueUrl =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';
  const actionCodeSettings: ActionCodeSettings = {
    url: configuredContinueUrl || defaultContinueUrl,
    handleCodeInApp: false,
  };

  if (configuredDynamicLinkDomain) {
    actionCodeSettings.dynamicLinkDomain = configuredDynamicLinkDomain;
  }

  return actionCodeSettings;
};

const buildPasswordResetActionCodeSettings = (): ActionCodeSettings => {
  const configuredContinueUrl = String(
    process.env.REACT_APP_PASSWORD_RESET_CONTINUE_URL || ''
  ).trim();
  const defaultContinueUrl =
    typeof window !== 'undefined' && window.location?.origin
      ? `${window.location.origin}/auth/action`
      : 'http://localhost/auth/action';

  const actionCodeSettings: ActionCodeSettings = {
    url: configuredContinueUrl || defaultContinueUrl,
    handleCodeInApp: false,
  };

  return actionCodeSettings;
};

const mapVerificationEmailError = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/too-many-requests':
      return 'Wysłano zbyt wiele próśb o weryfikację. Spróbuj ponownie za chwilę.';
    case 'auth/invalid-continue-uri':
    case 'auth/missing-continue-uri':
      return 'Konfiguracja linku weryfikacyjnego jest nieprawidłowa (continue URL). Skontaktuj się z administratorem.';
    case 'auth/unauthorized-continue-uri':
      return 'Domena continue URL nie jest autoryzowana w Firebase Auth. Skontaktuj się z administratorem.';
    case 'auth/invalid-dynamic-link-domain':
      return 'Konfiguracja dynamic link domain dla maila weryfikacyjnego jest nieprawidłowa.';
    case 'auth/invalid-sender':
      return 'Konfiguracja nadawcy email w Firebase jest nieprawidłowa.';
    case 'auth/user-token-expired':
    case 'auth/requires-recent-login':
      return 'Sesja wygasła. Zaloguj się ponownie i wyślij link jeszcze raz.';
    case 'auth/network-request-failed':
      return 'Nie udało się połączyć z usługą wysyłki. Sprawdź połączenie i spróbuj ponownie.';
    case 'auth/operation-not-allowed':
      return 'Wysyłka maili weryfikacyjnych jest wyłączona w Firebase Auth.';
    default:
      return 'Nie udało się wysłać maila weryfikacyjnego. Spróbuj ponownie.';
  }
};

const mapPasswordResetError = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'Nie znaleziono konta dla podanego adresu email.';
    case 'auth/invalid-email':
      return 'Podany adres email jest nieprawidłowy.';
    case 'auth/too-many-requests':
      return 'Wysłano zbyt wiele próśb. Spróbuj ponownie za chwilę.';
    case 'auth/missing-continue-uri':
    case 'auth/invalid-continue-uri':
    case 'auth/unauthorized-continue-uri':
      return 'Konfiguracja linku resetu hasła jest nieprawidłowa.';
    case 'auth/network-request-failed':
      return 'Nie udało się połączyć z usługą resetu hasła. Spróbuj ponownie.';
    case 'auth/operation-not-allowed':
      return 'Reset hasła dla kont email/hasło jest wyłączony.';
    default:
      return 'Nie udało się wysłać linku resetu hasła. Spróbuj ponownie.';
  }
};

const sendVerificationEmail = async (
  user: User,
  options: VerificationEmailOptions
): Promise<VerificationEmailResult> => {
  const requestId = `verify_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const source = options.source;
  const actionCodeSettings = buildVerificationActionCodeSettings();
  const startedAt = Date.now();
  console.info('[AUTH][VERIFY_EMAIL] send start', {
    requestId,
    source,
    uid: user.uid,
    email: user.email,
    actionCodeSettings: actionCodeSettings
      ? {
          url: actionCodeSettings.url,
          hasDynamicLinkDomain: Boolean(actionCodeSettings.dynamicLinkDomain),
          dynamicLinkDomain: actionCodeSettings.dynamicLinkDomain || null,
          handleCodeInApp: actionCodeSettings.handleCodeInApp,
        }
      : null,
    firebaseAuthDomain: auth.app.options.authDomain || null,
    appOrigin:
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null,
  });
  try {
    await sendEmailVerification(user, actionCodeSettings);
    console.info('[AUTH][VERIFY_EMAIL] send success', {
      requestId,
      source,
      uid: user.uid,
      email: user.email,
      durationMs: Date.now() - startedAt,
    });
    return { success: true, requestId, source };
  } catch (error: any) {
    const errorCode = String(error?.code || 'unknown');
    console.error('[AUTH][VERIFY_EMAIL] send failed', {
      requestId,
      source,
      uid: user.uid,
      email: user.email,
      code: errorCode,
      message: String(error?.message || ''),
      customData: error?.customData || null,
      durationMs: Date.now() - startedAt,
      error,
    });
    return {
      success: false,
      errorCode,
      userMessage: mapVerificationEmailError(errorCode),
      requestId,
      source,
    };
  }
};

const requestPasswordReset = async (email: string): Promise<PasswordResetRequestResult> => {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    return {
      success: false,
      status: 'failed',
      userMessage: 'Podaj adres email, aby wysłać link resetu hasła.',
    };
  }

  const actionCodeSettings = buildPasswordResetActionCodeSettings();
  try {
    await sendPasswordResetEmail(auth, normalizedEmail, actionCodeSettings);
    return {
      success: true,
      status: 'sent',
      userMessage:
        'Jeśli konto istnieje, wysłaliśmy link resetu hasła. Sprawdź skrzynkę odbiorczą i spam.',
    };
  } catch (error: any) {
    const errorCode = String(error?.code || 'unknown');
    if (errorCode === 'auth/user-not-found') {
      return {
        success: false,
        status: 'user-not-found',
        errorCode,
        userMessage: mapPasswordResetError(errorCode),
      };
    }

    return {
      success: false,
      status: 'failed',
      errorCode,
      userMessage: mapPasswordResetError(errorCode),
    };
  }
};

const verifyPasswordResetOobCode = async (oobCode: string): Promise<string> => {
  const normalizedCode = String(oobCode || '').trim();
  if (!normalizedCode) {
    throw new Error('Brak kodu resetu hasła.');
  }
  return verifyPasswordResetCode(auth, normalizedCode);
};

const confirmPasswordResetWithCode = async (
  oobCode: string,
  newPassword: string
): Promise<void> => {
  const normalizedCode = String(oobCode || '').trim();
  const normalizedPassword = String(newPassword || '');

  if (!normalizedCode) {
    throw new Error('Brak kodu resetu hasła.');
  }
  const passwordValidation = await validateAccountPassword(normalizedPassword);
  if (!passwordValidation.isValid) {
    const error = new Error(ACCOUNT_PASSWORD_POLICY_COPY) as Error & { code: string };
    error.code = 'auth/weak-password';
    throw error;
  }

  await confirmPasswordResetInAuth(auth, normalizedCode, normalizedPassword);
};

// Funkcja rejestracji
const register = async (
  email: string,
  password: string,
  displayName: string,
  role: UserRole
): Promise<RegisterResult> => {
  let user: User | null = null; // Keep track of the created user
  try {
    console.log(`[INFO] Rozpoczęcie rejestracji dla email: ${email}, rola: ${role}`);
    const passwordValidation = await validateAccountPassword(password);
    if (!passwordValidation.isValid) {
      const error = new Error(ACCOUNT_PASSWORD_POLICY_COPY) as Error & { code: string };
      error.code = 'auth/weak-password';
      throw error;
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    user = userCredential.user; // Assign the created user
    console.log(`[INFO] Użytkownik utworzony w Auth: ${user.uid}`);

    // Canonical profile producer for signup lives in backend callable.
    console.log(`[INFO] Próba utworzenia dokumentu profilu przez backend callable dla ${user.uid}`);
    if (role !== UserRole.CLIENT && role !== UserRole.SERVICE_ADMIN) {
      throw new Error('Rejestracja wspiera tylko role client i serviceAdmin.');
    }
    const signupRole: 'client' | 'serviceAdmin' =
      role === UserRole.SERVICE_ADMIN ? 'serviceAdmin' : 'client';
    await provisionUserProfileOnSignup({
      role: signupRole,
      displayName: String(displayName || '').trim(),
    });

    if (
      displayName &&
      auth.currentUser &&
      auth.currentUser.uid === user.uid &&
      auth.currentUser.displayName !== displayName
    ) {
      try {
        await updateProfile(auth.currentUser, { displayName });
      } catch (profileUpdateError) {
        console.error(
          '[ERROR] Błąd podczas aktualizacji displayName w profilu Auth:',
          profileUpdateError
        );
      }
    }

    // Send verification email AFTER successful profile creation.
    const verificationEmail = await sendVerificationEmail(user, { source: 'register' });

    console.log(`[SUCCESS] Rejestracja zakończona pomyślnie dla ${user.uid}`);
    return { user, verificationEmail };
  } catch (error: any) {
    console.error('[ERROR] Błąd podczas procesu rejestracji (authService.register):', error);
    // Log the specific error code and message if available from Firestore or Auth
    if (error instanceof Error && 'code' in error) {
      console.error(
        `[ERROR] Firebase error code: ${(error as any).code}, message: ${error.message}`
      );
    }

    // Optional: If profile creation fails, consider deleting the Auth user for consistency
    // This can be risky if the deletion itself fails. Handle with care.
    // if (user) {
    //   console.log(`[INFO] Próba usunięcia użytkownika Auth (${user.uid}) po błędzie tworzenia profilu Firestore.`);
    //   try {
    //      await user.delete();
    //      console.log(`[SUCCESS] Usunięto użytkownika Auth (${user.uid}).`);
    //   } catch (deleteError) {
    //      console.error(`[ERROR] Błąd podczas usuwania użytkownika Auth (${user.uid}):`, deleteError);
    //   }
    // }
    throw error; // Re-throw the original error to be handled by the UI form
  }
};

const supportsTotpSdk = (): boolean =>
  typeof TotpMultiFactorGenerator?.assertionForSignIn === 'function' &&
  typeof TotpMultiFactorGenerator?.assertionForEnrollment === 'function' &&
  typeof TotpMultiFactorGenerator?.generateSecret === 'function' &&
  typeof getMultiFactorResolver === 'function';

const isTotpHint = (hint: MultiFactorInfo): boolean =>
  String(hint.factorId) === TotpMultiFactorGenerator.FACTOR_ID || String(hint.factorId) === 'totp';

const buildTotpSignInChallenge = (error: unknown): TotpSignInChallenge | null => {
  const firebaseError = error as Partial<MultiFactorError> | null;
  if (firebaseError?.code !== 'auth/multi-factor-auth-required' || !supportsTotpSdk()) {
    return null;
  }

  try {
    const resolver = getMultiFactorResolver(auth, firebaseError as MultiFactorError);
    const hints = resolver.hints.filter(isTotpHint).map((hint) => ({
      uid: hint.uid,
      displayName: hint.displayName || null,
      factorId: hint.factorId,
    }));

    if (!hints.length) {
      return null;
    }

    return {
      kind: 'totp-sign-in',
      resolver,
      hints,
      selectedEnrollmentId: hints[0].uid,
    };
  } catch (resolverError) {
    console.error('[AUTH][MFA] Nie udało się odczytać resolvera MFA:', resolverError);
    return null;
  }
};

const wrapMfaRequiredError = (error: unknown): never => {
  const mfaChallenge = buildTotpSignInChallenge(error);
  if (!mfaChallenge) {
    throw error;
  }

  const wrappedError = new Error('Wymagana jest weryfikacja dwuetapowa.') as MfaRequiredSignInError;
  wrappedError.name = 'MfaRequiredSignInError';
  wrappedError.code = 'auth/multi-factor-auth-required';
  wrappedError.mfaChallenge = mfaChallenge;
  wrappedError.originalError = error;
  throw wrappedError;
};

const isMfaRequiredError = (error: unknown): error is MfaRequiredSignInError => {
  const candidate = error as Partial<MfaRequiredSignInError> | null;
  return (
    candidate?.code === 'auth/multi-factor-auth-required' &&
    candidate.mfaChallenge?.kind === 'totp-sign-in'
  );
};

const completeTotpSignIn = async (
  challenge: TotpSignInChallenge,
  oneTimePassword: string
): Promise<User> => {
  const normalizedCode = String(oneTimePassword || '').replace(/\s+/g, '');
  if (!normalizedCode) {
    const error = new Error('Podaj kod z aplikacji uwierzytelniającej.') as Error & {
      code: string;
    };
    error.code = 'auth/missing-verification-code';
    throw error;
  }

  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    challenge.selectedEnrollmentId,
    normalizedCode
  );
  const result = await challenge.resolver.resolveSignIn(assertion);
  return result.user;
};

const completeTotpReauthentication = async (
  challenge: TotpSignInChallenge,
  oneTimePassword: string
): Promise<User> => {
  return completeTotpSignIn(challenge, oneTimePassword);
};

const getCurrentTotpFactor = (user: User): MultiFactorInfo | null => {
  const enrolledFactors = multiFactor(user).enrolledFactors || [];
  return enrolledFactors.find(isTotpHint) || null;
};

const startTotpEnrollment = async (): Promise<TotpEnrollmentDraft> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Użytkownik nie jest zalogowany.');
  }
  if (!supportsTotpSdk()) {
    const error = new Error('Weryfikacja dwuetapowa jest chwilowo niedostępna.') as Error & {
      code: string;
    };
    error.code = 'auth/totp-sdk-unavailable';
    throw error;
  }

  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const qrCodeUrl = secret.generateQrCodeUrl(user.email || user.uid, 'Ratuj Sprzęt');

  return {
    secret,
    secretKey: secret.secretKey,
    qrCodeUrl,
    codeLength: secret.codeLength,
    codeIntervalSeconds: secret.codeIntervalSeconds,
    enrollmentCompletionDeadline: secret.enrollmentCompletionDeadline,
  };
};

const finalizeTotpEnrollment = async (
  secret: TotpSecret,
  oneTimePassword: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Użytkownik nie jest zalogowany.');
  }

  const normalizedCode = String(oneTimePassword || '').replace(/\s+/g, '');
  if (!normalizedCode) {
    const error = new Error('Podaj kod z aplikacji uwierzytelniającej.') as Error & {
      code: string;
    };
    error.code = 'auth/missing-verification-code';
    throw error;
  }

  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, normalizedCode);
  await multiFactor(user).enroll(assertion, 'Ratuj Sprzęt');
  await user.reload();
};

const disableTotpEnrollment = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Użytkownik nie jest zalogowany.');
  }

  const factor = getCurrentTotpFactor(user);
  if (!factor) {
    const error = new Error('Konto nie ma aktywnej weryfikacji dwuetapowej.') as Error & {
      code: string;
    };
    error.code = 'auth/totp-not-enrolled';
    throw error;
  }

  await multiFactor(user).unenroll(factor);
  await user.reload();
};

const getTotpMfaReadiness = (user?: User | null): TotpMfaReadiness => {
  const sdkSupportsTotp = supportsTotpSdk();
  const deploymentConfigured = sdkSupportsTotp;

  let enrolledFactorsCount = 0;
  let hasTotpEnrollment = false;

  if (user && sdkSupportsTotp) {
    try {
      const enrolledFactors = multiFactor(user).enrolledFactors || [];
      enrolledFactorsCount = enrolledFactors.length;
      hasTotpEnrollment = enrolledFactors.some(isTotpHint);
    } catch (error) {
      console.warn('[AUTH][MFA] Nie udało się odczytać enrollment MFA:', error);
    }
  }

  const canManageEnrollment = sdkSupportsTotp && deploymentConfigured;
  const status: TotpMfaReadiness['status'] = !sdkSupportsTotp
    ? 'unavailable'
    : hasTotpEnrollment
      ? 'active'
      : 'not-enrolled';

  return {
    sdkSupportsTotp,
    deploymentConfigured,
    canManageEnrollment,
    enrolledFactorsCount,
    hasTotpEnrollment,
    status,
  };
};

// Funkcja logowania
const login = async (email: string, password: string): Promise<User> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Błąd logowania w authService:', error);
    if ((error as { code?: string })?.code === 'auth/multi-factor-auth-required') {
      wrapMfaRequiredError(error);
    }
    throw error;
  }
};

// Funkcja wylogowania
const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Błąd wylogowania w authService:', error);
    throw error;
  }
};

// Funkcja logowania przez Google
const signInWithGoogle = async (): Promise<User> => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Sprawdź, czy to nowy użytkownik wg Firebase Auth
    // const additionalUserInfo = getAdditionalUserInfo(result); // Not strictly needed for this logic

    // --- BEGIN FIX: Explicitly update photoURL in Firebase Auth profile ---
    // Check if auth.currentUser exists and matches the user being processed
    if (
      user.photoURL &&
      auth.currentUser &&
      auth.currentUser.uid === user.uid &&
      auth.currentUser.photoURL !== user.photoURL
    ) {
      try {
        await updateProfile(auth.currentUser, { photoURL: user.photoURL });
        console.log(`[INFO] Zaktualizowano photoURL w profilu Auth dla ${user.uid}`);
      } catch (profileUpdateError) {
        console.error(
          '[ERROR] Błąd podczas aktualizacji photoURL w profilu Auth:',
          profileUpdateError
        );
        // Continue even if profile update fails, login was successful
      }
    }
    // --- END FIX ---

    return user;
  } catch (error: any) {
    console.error('[ERROR] Błąd logowania przez Google w authService:', error);
    if (error?.code === 'auth/multi-factor-auth-required') {
      wrapMfaRequiredError(error);
    }
    // Można dodać mapowanie błędów specyficznych dla Google, np. 'auth/popup-closed-by-user'
    throw error; // Re-throw error for handling in UI
  }
};

const reauthenticateWithGoogle = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Użytkownik nie jest zalogowany.');
  }

  const provider = new GoogleAuthProvider();
  try {
    await reauthenticateWithPopup(user, provider);
  } catch (error: any) {
    if (error?.code === 'auth/multi-factor-auth-required') {
      wrapMfaRequiredError(error);
    }
    throw error;
  }
};

// Funkcja aktualizacji nazwy wyświetlanej
const updateUserDisplayName = async (user: User, newDisplayName: string): Promise<void> => {
  if (!user) {
    throw new Error('Użytkownik nie jest zalogowany');
  }

  try {
    // Aktualizuj w Firebase Auth (only if different)
    if (user.displayName !== newDisplayName) {
      await updateProfile(user, { displayName: newDisplayName });
    }

    // Aktualizuj w Firestore
    const userRef = doc(firestore, 'users', user.uid);
    await updateDoc(userRef, {
      displayName: newDisplayName,
      updatedAt: serverTimestamp(),
    });

    console.log('[SUCCESS] Pomyślnie zaktualizowano displayName');
  } catch (error) {
    console.error('[ERROR] Błąd podczas aktualizacji displayName:', error);
    throw error;
  }
};

// Funkcja aktualizacji hasła
const updateUserPassword = async (newPassword: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Użytkownik nie jest zalogowany');
  }

  const passwordValidation = await validateAccountPassword(newPassword);
  if (!passwordValidation.isValid) {
    const error = new Error(ACCOUNT_PASSWORD_POLICY_COPY) as Error & { code: string };
    error.code = 'auth/weak-password';
    throw error;
  }

  try {
    await updatePassword(user, newPassword);
    console.log('[SUCCESS] Hasło zostało pomyślnie zaktualizowane.');
  } catch (error) {
    console.error('[ERROR] Błąd podczas aktualizacji hasła:', error);
    throw error;
  }
};

const reauthenticateWithPassword = async (password: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('Użytkownik nie jest zalogowany');
  }

  const normalizedPassword = String(password || '');
  if (!normalizedPassword) {
    throw new Error('Podaj aktualne hasło.');
  }

  const credential = EmailAuthProvider.credential(user.email, normalizedPassword);
  try {
    await reauthenticateWithCredential(user, credential);
  } catch (error: any) {
    if (error?.code === 'auth/multi-factor-auth-required') {
      wrapMfaRequiredError(error);
    }
    throw error;
  }
};

// Funkcja mapująca kody błędów Firebase Auth na komunikaty po polsku
const mapFirebaseError = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'Nieprawidłowy format adresu e-mail.';
    case 'auth/user-disabled':
      return 'Konto użytkownika zostało wyłączone.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Nieprawidłowy e-mail lub hasło.';
    case 'auth/email-already-in-use':
      return 'To konto już istnieje. Zaloguj się tym adresem i dokończ weryfikację email, jeśli jeszcze jej nie potwierdzono.';
    case 'auth/weak-password':
      return ACCOUNT_PASSWORD_POLICY_COPY;
    case 'auth/operation-not-allowed':
      return 'Ta metoda logowania lub weryfikacji jest chwilowo niedostępna.';
    case 'auth/requires-recent-login':
      return 'To działanie wymaga ponownego zalogowania się. Wyloguj się i zaloguj ponownie.';
    case 'auth/multi-factor-auth-required':
      return 'Potwierdź logowanie kodem z aplikacji uwierzytelniającej.';
    case 'auth/invalid-verification-code':
      return 'Kod weryfikacyjny jest nieprawidłowy. Sprawdź aplikację uwierzytelniającą i spróbuj ponownie.';
    case 'auth/missing-verification-code':
      return 'Podaj kod z aplikacji uwierzytelniającej.';
    case 'auth/network-request-failed':
      return 'Nie udało się połączyć z usługą. Sprawdź internet i spróbuj ponownie.';
    case 'auth/totp-sdk-unavailable':
    case 'auth/totp-not-enrolled':
      return 'Weryfikacja dwuetapowa jest chwilowo niedostępna. Spróbuj później.';
    case 'auth/user-mismatch':
    case 'auth/provider-mismatch':
    case 'auth/account-exists-with-different-credential':
    case 'auth/provider-already-linked':
    case 'auth/credential-already-in-use':
      return 'To działanie wymaga potwierdzenia tym samym sposobem logowania, którego użyto dla konta.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Potwierdzenie przez Google zostało przerwane.';
    // Firestore specific errors (if caught here, though usually caught earlier)
    case 'permission-denied': // Added Firestore permission error
      return 'Brak uprawnień do wykonania tej operacji w bazie danych.';
    default:
      console.error('[WARNING] Nieznany kod błędu Firebase:', errorCode); // Log unknown errors
      return 'Wystąpił nieznany błąd. Spróbuj ponownie później.';
  }
};

export const authService = {
  register,
  login,
  logout,
  signInWithGoogle, // Add Google sign-in
  isMfaRequiredError,
  completeTotpSignIn,
  completeTotpReauthentication,
  getTotpMfaReadiness,
  startTotpEnrollment,
  finalizeTotpEnrollment,
  disableTotpEnrollment,
  sendVerificationEmail,
  requestPasswordReset,
  verifyPasswordResetOobCode,
  confirmPasswordResetWithCode,
  // createUserProfileDocument, // Keep internal unless needed elsewhere
  mapFirebaseError,
  updateUserDisplayName,
  updateUserPassword,
  reauthenticateWithPassword,
  reauthenticateWithGoogle,
};
